import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { activitiesService, tagsService, partnersService, bookmarkService, mediaUrl } from '../lib/api-service';
import { useAuth } from '../lib/auth';
import type { ActivityListItem, Tag, PartnerListItem } from '../lib/types';
import { PartnersSponsorsStrip } from '../components/PartnersSponsorsStrip';
import { getTextColorHex } from '../utils/color-contrast';
import { formatDate, formatTime } from '../utils/date';
import { priceFromForActivity } from '../utils/participant-types';
import { FilterChip } from '../components/FilterChip';
import { PartnerAutocomplete } from '../components/PartnerAutocomplete';
import { Search, X, SlidersHorizontal, Calendar, Handshake, Tag as TagIcon, Loader2, Users, CalendarCheck, Heart, HandHelping, Star } from 'lucide-react';
import { DocumentMeta } from '../hooks/useDocumentMeta';
import { DateInput } from '../components/DateInput';
import { Checkbox } from '../components/ui/checkbox';

export default function EventsList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data
  const [activities, setActivities] = useState<ActivityListItem[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [allPartners, setAllPartners] = useState<PartnerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Bookmarks
  const [bookmarkedSlugs, setBookmarkedSlugs] = useState<Set<string>>(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedMainTags, setSelectedMainTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [volunteerOpen, setVolunteerOpen] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounce search
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Load tags + partners + bookmarks once
  useEffect(() => {
    Promise.all([
      tagsService.list(),
      partnersService.list(),
    ]).then(([tags, partners]) => {
      setAllTags(tags.results);
      setAllPartners(partners.results);
    });
    if (user) {
      bookmarkService.myBookmarks()
        .then((items) => setBookmarkedSlugs(new Set(items.map((a) => a.slug))))
        .catch(() => {});
    }
  }, [user]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selectedMainTags.length) params.set('main_tags', selectedMainTags.join(','));
    if (selectedTags.length) params.set('tags', selectedTags.join(','));
    if (selectedPartner) params.set('partner', selectedPartner);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (volunteerOpen) params.set('volunteer_open', 'true');
    if (includePast) params.set('future_only', 'false');
    return params.toString();
  }, [debouncedSearch, selectedMainTags, selectedTags, selectedPartner, dateFrom, dateTo, volunteerOpen, includePast]);

  // Fetch activities
  useEffect(() => {
    setLoading(true);
    const qs = buildParams();
    const params: Record<string, string> = {};
    new URLSearchParams(qs).forEach((v, k) => { params[k] = v; });
    activitiesService.list(Object.keys(params).length ? params : undefined)
      .then((data) => {
        setActivities(data.results);
        setNextPage(data.next);
        setTotalCount(data.count);
      })
      .finally(() => setLoading(false));
  }, [buildParams]);

  async function loadMore() {
    if (!nextPage) return;
    setLoadingMore(true);
    try {
      const data = await activitiesService.listPage(nextPage);
      setActivities((prev) => [...prev, ...data.results]);
      setNextPage(data.next);
    } finally {
      setLoadingMore(false);
    }
  }

  const mainTags = allTags.filter((t) => t.is_main_tag);
  const normalTags = allTags.filter((t) => !t.is_main_tag);

  function toggleSlugList(list: string[], slug: string): string[] {
    return list.includes(slug) ? list.filter((s) => s !== slug) : [...list, slug];
  }

  async function toggleBookmark(e: React.MouseEvent, slug: string) {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;
    const isBookmarked = bookmarkedSlugs.has(slug);
    // Optimistic update
    setBookmarkedSlugs((prev) => {
      const next = new Set(prev);
      if (isBookmarked) next.delete(slug);
      else next.add(slug);
      return next;
    });
    try {
      if (isBookmarked) await bookmarkService.remove(slug);
      else await bookmarkService.add(slug);
    } catch {
      // Revert on error
      setBookmarkedSlugs((prev) => {
        const next = new Set(prev);
        if (isBookmarked) next.add(slug);
        else next.delete(slug);
        return next;
      });
    }
  }

  const activeFilterCount =
    selectedMainTags.length + selectedTags.length + (selectedPartner ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (volunteerOpen ? 1 : 0) + (includePast ? 1 : 0);

  function clearFilters() {
    setSelectedMainTags([]);
    setSelectedTags([]);
    setSelectedPartner('');
    setVolunteerOpen(false);
    setIncludePast(false);
    setDateFrom('');
    setDateTo('');
    setSearch('');
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <DocumentMeta
        title="Evenimente"
        description="Descoperă activitățile și evenimentele din cadrul programului Alba Iulia — Capitala Tineretului din România 2026."
        url="/evenimente"
      />
      <h1 className="sr-only">
        Alba Iulia, Capitala Tineretului din România 2026 — evenimente și activități pentru tineri din Capitala Tineretului
      </h1>
      {/* #104/#105 — About Us homepage blurb kept dark for launch; re-enable when final copy lands.
      <Link
        to="/despre-noi"
        className="mb-4 block overflow-hidden rounded-xl border bg-gradient-to-br from-[var(--vivid-purple)]/5 to-[var(--vivid-yellow-green)]/10 p-4 transition-colors hover:from-[var(--vivid-purple)]/10 hover:to-[var(--vivid-yellow-green)]/20 sm:p-5"
      >
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--vivid-purple)]" style={{ fontFamily: 'var(--font-primary)' }}>
          Capitala Tineretului din România 2026
        </p>
        <h2 className="mb-1.5 text-base font-bold leading-tight sm:text-lg" style={{ fontFamily: 'var(--font-primary)' }}>
          Alba Iulia, anul tinerilor
        </h2>
        <p className="text-xs text-muted-foreground sm:text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
          Un an de evenimente, oportunități și întâlniri sub umbrela Capital. Descoperă mai multe despre program →
        </p>
      </Link>
      */}

      {/* My events link for logged-in users */}
      {user && (
        <Link
          to="/cont/evenimente"
          className="mb-4 flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)]/5 px-4 py-2.5 text-sm font-medium text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/10"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <CalendarCheck className="h-4 w-4" />
          Evenimentele mele
        </Link>
      )}

      {/* Search + filter button */}
      <div className="mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Caută evenimente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-full border py-2.5 pl-10 pr-9 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`relative flex items-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-medium transition-all active:scale-95 ${
            showFilters ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5 text-[var(--vivid-purple)]' : 'border-gray-200 bg-white'
          }`}
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtre</span>
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--vivid-orange)] text-xs font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Quick main tag chips */}
      {mainTags.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {user && (
            <button
              onClick={() => setVolunteerOpen(!volunteerOpen)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                volunteerOpen ? 'border-transparent bg-green-600 text-white' : 'border-gray-200 bg-white'
              }`}
            >
              <HandHelping className="h-3.5 w-3.5" />
              Voluntariat
            </button>
          )}
          {mainTags.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedMainTags(toggleSlugList(selectedMainTags, t.slug))}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedMainTags.includes(t.slug) ? 'border-transparent' : 'border-gray-200 bg-white'
              }`}
              style={selectedMainTags.includes(t.slug) ? { backgroundColor: t.color || '#3b0b86', color: getTextColorHex(t.color || '#3b0b86') } : {}}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      {/* Full filter panel */}
      {showFilters && (
        <div className="mb-4 space-y-5 rounded-xl border bg-white p-5 shadow-sm">
          {/* Partners */}
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
              <Handshake className="h-4 w-4 text-[var(--vivid-purple)]" /> Organizator
            </div>
            <PartnerAutocomplete
              partners={allPartners}
              value={selectedPartner}
              onChange={setSelectedPartner}
              placeholder="Caută după organizator..."
            />
          </div>

          {/* Date range */}
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
              <Calendar className="h-4 w-4 text-[var(--vivid-purple)]" /> Perioada
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">De la</label>
                <DateInput value={dateFrom} onChange={setDateFrom} className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)]" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Până la</label>
                <DateInput value={dateTo} onChange={setDateTo} className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[var(--vivid-purple)]" />
              </div>
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
              <Checkbox checked={includePast} onCheckedChange={(checked) => setIncludePast(checked === true)} />
              Include evenimente trecute
            </label>
          </div>

          {/* Other tags */}
          {normalTags.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                <TagIcon className="h-4 w-4 text-[var(--vivid-purple)]" /> Categorii
              </div>
              <div className="flex flex-wrap gap-2">
                {normalTags.map((t) => (
                  <FilterChip
                    key={t.id}
                    label={t.name}
                    selected={selectedTags.includes(t.slug)}
                    onToggle={() => setSelectedTags(toggleSlugList(selectedTags, t.slug))}
                  />
                ))}
              </div>
            </div>
          )}

          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-sm font-medium text-[var(--vivid-purple)] hover:underline">
              Șterge filtrele
            </button>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterCount > 0 && !showFilters && (
        <div className="mb-4 flex flex-wrap gap-2">
          {selectedPartner && (
            <FilterChip
              label={`Organizator: ${allPartners.find((p) => p.slug === selectedPartner)?.name || selectedPartner}`}
              selected onRemove={() => setSelectedPartner('')}
            />
          )}
          {selectedMainTags.map((slug) => (
            <FilterChip key={slug} label={allTags.find((t) => t.slug === slug)?.name || slug} selected onRemove={() => setSelectedMainTags(selectedMainTags.filter((s) => s !== slug))} />
          ))}
          {selectedTags.map((slug) => (
            <FilterChip key={slug} label={allTags.find((t) => t.slug === slug)?.name || slug} selected onRemove={() => setSelectedTags(selectedTags.filter((s) => s !== slug))} />
          ))}
          {dateFrom && <FilterChip label={`De la: ${dateFrom}`} selected onRemove={() => setDateFrom('')} />}
          {dateTo && <FilterChip label={`Până la: ${dateTo}`} selected onRemove={() => setDateTo('')} />}
          {includePast && <FilterChip label="Include evenimente trecute" selected onRemove={() => setIncludePast(false)} />}
        </div>
      )}

      {/* Results count */}
      <p className="mb-4 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        {loading ? 'Se încarcă...' : `${totalCount} ${totalCount === 1 ? 'eveniment' : 'evenimente'}`}
      </p>

      {/* Activity cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
        </div>
      ) : activities.length > 0 ? (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {activities.map((a, idx) => {
              const mainTag = a.tags.find((t) => t.is_main_tag);
              const isLcpCandidate = idx === 0;
              const priceFrom = priceFromForActivity(a.participant_types);
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(`/eveniment/${a.slug}`)}
                  className="group overflow-hidden rounded-2xl border bg-white text-left shadow-sm transition-all hover:shadow-lg active:scale-[0.98]"
                >
                  {/* Banner */}
                  <div className="relative h-40 bg-gradient-to-br from-[var(--vivid-purple)]/20 to-[var(--vivid-cyan)]/10">
                    {a.banner_image && (
                      <img
                        src={mediaUrl(a.banner_image)}
                        alt={a.title}
                        loading={isLcpCandidate ? 'eager' : 'lazy'}
                        fetchPriority={isLcpCandidate ? 'high' : 'auto'}
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    )}
                    <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                      {a.is_featured && (
                        <span className="flex items-center gap-1 rounded-full bg-[var(--vivid-yellow-green)] px-2.5 py-0.5 text-[10px] font-semibold text-black">
                          <Star className="h-3 w-3" fill="currentColor" />
                          Important
                        </span>
                      )}
                      {!a.is_published && a.is_volunteer_visible && (
                        <span className="flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-[10px] font-semibold text-white">
                          <HandHelping className="h-3 w-3" />
                          Doar voluntari
                        </span>
                      )}
                      {mainTag && (
                        <span
                          className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
                          style={{ backgroundColor: mainTag.color || '#3b0b86', color: getTextColorHex(mainTag.color || '#3b0b86') }}
                        >
                          {mainTag.name}
                        </span>
                      )}
                    </div>
                    {user && (
                      <button
                        onClick={(e) => toggleBookmark(e, a.slug)}
                        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm transition-all hover:scale-110 active:scale-95"
                        aria-label={bookmarkedSlugs.has(a.slug) ? 'Elimină de la favorite' : 'Adaugă la favorite'}
                      >
                        <Heart
                          className="h-4 w-4"
                          fill={bookmarkedSlugs.has(a.slug) ? '#ff6417' : 'none'}
                          stroke={bookmarkedSlugs.has(a.slug) ? '#ff6417' : '#666'}
                        />
                      </button>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="mb-2 text-sm font-bold leading-snug" style={{ fontFamily: 'var(--font-primary)' }}>
                      {a.title}
                    </h3>
                    <div className="space-y-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      <div>{formatDate(a.start_datetime)} · {formatTime(a.start_datetime)}</div>
                      {a.location_text && <div>{a.location_text}</div>}
                      {a.partners && a.partners.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 flex-shrink-0" />
                          <span className="line-clamp-1">{a.partners.map((p) => p.name).join(', ')}</span>
                        </div>
                      )}
                    </div>
                    {a.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {a.tags.filter((t) => !t.is_main_tag).slice(0, 3).map((t) => (
                          <span key={t.id} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {t.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {priceFrom !== null && (
                      <div className="mt-3 inline-block rounded-full bg-[var(--vivid-yellow-green)]/30 px-2.5 py-0.5 text-[10px] font-semibold text-[var(--vivid-purple)]">
                        De la {priceFrom} RON
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Load more */}
          {nextPage && (
            <div className="mt-8 text-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-full border-2 border-[var(--vivid-purple)] px-8 py-2.5 text-sm font-semibold text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/5 disabled:opacity-50"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                {loadingMore ? 'Se încarcă...' : 'Încarcă mai multe'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center py-16 text-center">
          <Search className="mb-3 h-12 w-12 text-muted-foreground/30" />
          <h3 className="mb-2 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Nu am găsit evenimente
          </h3>
          <p className="mb-4 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            Încearcă să ajustezi filtrele sau căutarea.
          </p>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-sm font-medium text-[var(--vivid-purple)] hover:underline">
              Resetează filtrele
            </button>
          )}
        </div>
      )}

      <PartnersSponsorsStrip />
    </div>
  );
}
